/**
 * VP Reviews section: move app block(s) to header slot, render rating row, Loox helpers, mobile carousel.
 *
 * Global API:
 *   window.vpReviewsRenderRatingRow(container, options)
 *   window.vpReviewsGetLooxSummary(sectionOrAppSlot)
 *   window.vpReviewsGetLooxSummaryWhenReady(sectionOrAppSlot, options?)
 */
const CARD_WIDTH_MOBILE = 216; // 200px card + 16px gap
const LOOX_READY_DEFAULT_TIMEOUT = 10000;

const STAR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="vp-review-star" aria-hidden="true" focusable="false"><rect width="18" height="18" fill="#0EAB00"/><path d="M4.36863 15.75L5.58738 10.4813L1.49988 6.9375L6.89988 6.46875L8.99988 1.5L11.0999 6.46875L16.4999 6.9375L12.4124 10.4813L13.6311 15.75L8.99988 12.9562L4.36863 15.75Z" fill="white"/></svg>';

function waitForElem(waitFor, callback, minElements = 1, isVariable = false, timer = 30000, frequency = 25) {
  let elements = isVariable ? window[waitFor] : document.querySelectorAll(waitFor);
  if (timer <= 0) return;
  (!isVariable && elements.length >= minElements) || (isVariable && typeof window[waitFor] !== "undefined")
    ? callback(elements)
    : setTimeout(() => waitForElem(waitFor, callback, minElements, isVariable, timer - frequency), frequency);
}

/**
 * Builds the Figma 1-1689 rating row (5 stars + text) as a DOM element.
 * @param {string|number} score - e.g. 4.9
 * @param {string|number} reviewCount - e.g. 10000 (will be formatted with commas)
 * @returns {HTMLElement} The rating row element (not yet in the DOM)
 */
function renderRatingRow(score, reviewCount) {
  return `<div class="vp-reviews__rating-row">${STAR_SVG.repeat(5)}<p class="vp-review-text">${score} from ${reviewCount} reviews</p></div>`;
}

/**
 * Binds progress bar, counter, and prev/next to Swiper (nav elements live outside the swiper root).
 */
function bindCarouselPagination(swiper, sectionRoot, selectors) {
  const progressEl = sectionRoot.querySelector(selectors.progress);
  const counterEl = sectionRoot.querySelector(selectors.counter);
  const prevBtn = sectionRoot.querySelector(selectors.prev);
  const nextBtn = sectionRoot.querySelector(selectors.next);

  const update = () => {
    const total = swiper.slides?.length || 0;
    let currentIndex = swiper.realIndex; 
    let current;
    if (swiper.isEnd) {
        current = total;
    } else {
        const spv = swiper.params.slidesPerView
        current = Math.min(Math.floor(currentIndex + spv), total);
    }
    if (total === 0) current = 0;
    const pct = total > 0 ? (current / total) * 100 : 0;

    if (progressEl) progressEl.style.width = `${pct}%`;
    if (counterEl) {
      counterEl.textContent = `${String(current).padStart(2, "0")}/${String(total).padStart(2, "0")}`;
    }
   
  };

  update();
  swiper.on("slideChange transitionEnd", update);

  if (prevBtn) prevBtn.addEventListener("click", () => swiper.slidePrev());
  if (nextBtn) nextBtn.addEventListener("click", () => swiper.slideNext());
}

function buildSwiperSlider(sectionRoot, cardsContainer, sliderClass, navIdPrefix, breakPointSlider) {
  const cardCount = cardsContainer.children.length;
  if (cardCount === 0) return null;

  const sectionId =
    sectionRoot.dataset.sectionId ||
    (sectionRoot.closest("[id^='shopify-section-']")?.id || "").replace("shopify-section-", "");

  const swiperSlider = document.createElement("swiper-slider");
  swiperSlider.setAttribute("data-swiper-id", breakPointSlider);
  swiperSlider.className = sliderClass;
  swiperSlider.dataset.swiperConfig = JSON.stringify({
    navigation: {
      enabled: true,
      nextEl: sectionId ? `#${navIdPrefix}-next-${sectionId}` : `[data-${navIdPrefix}-next]`,
      prevEl: sectionId ? `#${navIdPrefix}-prev-${sectionId}` : `[data-${navIdPrefix}-prev]`,
    },
    pagination: { enabled: false },
    normalizeSlideIndex: true,
  });

  const swiperEl = document.createElement("div");
  swiperEl.className = "swiper";

  const wrapper = document.createElement("div");
  wrapper.className = "swiper-wrapper";
  wrapper.setAttribute("role", "list");
  wrapper.id = cardsContainer.id || "";
  wrapper.dataset.blockCount = cardsContainer.dataset.blockCount || String(cardCount);

  while (cardsContainer.firstElementChild) {
    const slide = document.createElement("div");
    slide.className = "swiper-slide";
    slide.appendChild(cardsContainer.firstElementChild);
    wrapper.appendChild(slide);
  }

  swiperEl.appendChild(wrapper);
  swiperSlider.appendChild(swiperEl);
  cardsContainer.parentNode.replaceChild(swiperSlider, cardsContainer);
  return swiperSlider;
}

/**
 * Classic Reviews: optional app blocks moved to header slot, then swiper.
 */
function initVpReviewsSection(section) {
  const appSlot = section.querySelector("[data-vp-reviews-app-slot]");
  const cardsContainer = section.querySelector("[data-vp-reviews-cards]");
  if (!cardsContainer) return;

  const appCount = parseInt(section.dataset.appBlockCount || "0", 10);
  if (appSlot) {
    for (let i = 0; i < appCount && cardsContainer.firstElementChild; i++) {
      appSlot.appendChild(cardsContainer.firstElementChild);
    }
  }

  const swiperSlider = buildSwiperSlider(section, cardsContainer, "vp-reviews__slider", "vp-reviews", "related-products-carousel");
  if (!swiperSlider) return;

  setTimeout(() => {
    const swiperInstance = swiperSlider.swiper;
    if (swiperInstance)
      bindCarouselPagination(swiperInstance, section, {
        progress: ".vp-reviews__pagination-progress",
        counter: ".vp-reviews__pagination-counter",
        prev: "[data-vp-reviews-prev]",
        next: "[data-vp-reviews-next]",
      });
  }, 0);
}

/** Pros / Figma layout: review cards only, no app block slot. */
function initVpReviewsProsSection(section) {
  const cardsContainer = section.querySelector("[data-vp-reviews-pros-cards]");
  if (!cardsContainer) return;

  const swiperSlider = buildSwiperSlider(section, cardsContainer, "vp-reviews-pros__slider", "vp-reviews-pros", "vp-review-pros-carousel");
  if (!swiperSlider) return;

  setTimeout(() => {
    const swiperInstance = swiperSlider.swiper;
    if (swiperInstance)
      bindCarouselPagination(swiperInstance, section, {
        progress: ".vp-reviews-pros__pagination-progress",
        counter: ".vp-reviews-pros__pagination-counter",
        prev: "[data-vp-reviews-pros-prev]",
        next: "[data-vp-reviews-pros-next]",
      });
  }, 0);
}

function initAllVpReviewsSections() {
  document.querySelectorAll(".vp-reviews").forEach(initVpReviewsSection);
  document.querySelectorAll(".vp-reviews-pros").forEach(initVpReviewsProsSection);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    customElements.whenDefined("swiper-slider").then(initAllVpReviewsSections);
  });
} else {
  customElements.whenDefined("swiper-slider").then(initAllVpReviewsSections);
}

document.addEventListener("shopify:section:load", (e) => {
  if (e.target.querySelector(".vp-reviews") || e.target.querySelector(".vp-reviews-pros")) {
    initAllVpReviewsSections();
  }
});

waitForElem(".loox-trust-badge-widget", (el) => {
  el.forEach((looxTrustBadgeWidget) => {
    const starsEl = looxTrustBadgeWidget.querySelector(".loox-trust-badge-widget-ratings__stars.loox-rating");
    const countEl = looxTrustBadgeWidget.querySelector(".loox-trust-badge-widget-ratings__raters-count");
    const score = starsEl?.dataset?.rating ?? "";
    const reviewCount = countEl?.textContent?.trim() ?? "";

    const ratingRow = renderRatingRow(score, reviewCount);

    document.querySelector(".vp-reviews__inner")?.insertAdjacentHTML("afterbegin", ratingRow);
  });
});
